/**
 * Ticket Table Component
 *
 * The moderator ticket list on the shared DataTable. Paging and ordering are the server's (the
 * parent passes one page plus the total), so the pager counts the real queue rather than whatever
 * page happened to load. Order is chosen with the page's Sort select, not column headers: the
 * server sorts each key in one fixed direction, and arrows that flip would promise otherwise.
 *
 * `mode="cards"` is the card view on every screen; on a phone both views are the compact card.
 */

import { useMemo, type ReactNode } from 'react'
import { Box, Typography, Chip } from '@mui/material'
import { TicketSummaryDto } from '@api/types/tickets'
import { TicketStatusBadge } from './TicketStatusBadge'
import { TicketPriorityIndicator } from './TicketPriorityIndicator'
import { TicketTypeIcon } from './TicketTypeIcon'
import { TicketEscalationChip } from './TicketEscalationChip'
import { TicketCardContent } from './TicketCard'
import { normalizeId, ticketScore } from './ticketActions'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { safeFormatDistanceToNow } from '@utils/dateUtils'
import { useAuthStore } from '@store/authStore'

interface TicketTableProps {
  tickets: TicketSummaryDto[]
  total?: number
  loading?: boolean
  error?: string | null
  onRetry?: () => void
  emptyMessage?: string
  onTicketClick?: (ticket: TicketSummaryDto) => void
  showAssignee?: boolean
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  mode?: 'auto' | 'cards'
  toolbar?: ReactNode
}

export function TicketTable({
  tickets,
  total,
  loading = false,
  error = null,
  onRetry,
  emptyMessage = 'No tickets found',
  onTicketClick,
  showAssignee = false,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  mode = 'auto',
  toolbar,
}: TicketTableProps) {
  const { user } = useAuthStore()

  const columns = useMemo<DataTableColumn<TicketSummaryDto>[]>(() => {
    const cols: DataTableColumn<TicketSummaryDto>[] = [
      {
        key: 'type',
        header: 'Type',
        width: 40,
        render: (ticket) => <TicketTypeIcon type={ticket.type} size="small" />,
      },
      {
        key: 'title',
        header: 'Title',
        primary: true,
        render: (ticket) => {
          const isClaimedByOther = ticket.claimedByUserId && normalizeId(ticket.claimedByUserId) !== normalizeId(user?.userId)
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
                {ticket.title}
              </Typography>
              {isClaimedByOther && (
                <Chip label="CLAIMED" size="small" color="warning" sx={{ fontSize: '0.7rem', height: 20 }} />
              )}
              {ticket.isEscalated && (
                <Chip label="ESCALATED" size="small" color="error" sx={{ fontSize: '0.7rem', height: 20 }} />
              )}
            </Box>
          )
        },
      },
      {
        key: 'status',
        header: 'Status',
        render: (ticket) => <TicketStatusBadge status={ticket.status} />,
      },
      {
        key: 'priority',
        header: 'Priority',
        render: (ticket) => <TicketPriorityIndicator priority={ticket.priority} />,
      },
      {
        key: 'escalation',
        header: 'Escalation',
        render: (ticket) => <TicketEscalationChip level={ticket.escalationLevel} label={ticket.escalationLabel} paused={ticket.escalationPaused} />,
      },
      {
        key: 'votes',
        header: 'Votes',
        align: 'center',
        hideBelow: 'md',
        render: (ticket) => {
          const score = ticketScore(ticket)
          return (
            <Typography variant="body2" color={score > 0 ? 'success.main' : score < 0 ? 'error.main' : 'text.secondary'}>
              {score > 0 ? `+${score}` : score}
            </Typography>
          )
        },
      },
    ]

    if (showAssignee) {
      cols.push({
        key: 'claimedBy',
        header: 'Claimed By',
        // Least essential column on a narrow table — first to go.
        hideBelow: 'md',
        render: (ticket) => (
          <Typography variant="caption" color="text.secondary">
            {ticket.claimedByDisplayName || '-'}
          </Typography>
        ),
      })
    }

    cols.push(
      {
        key: 'comments',
        header: 'Comments',
        align: 'center',
        hideBelow: 'md',
        render: (ticket) => <Typography variant="body2">{ticket.commentCount}</Typography>,
      },
      {
        key: 'lastUpdated',
        header: 'Last Updated',
        hideBelow: 'md',
        render: (ticket) => (
          <Typography variant="caption" color="text.secondary">
            {safeFormatDistanceToNow(ticket.lastUpdatedAt)}
          </Typography>
        ),
      }
    )

    return cols
  }, [showAssignee, user?.userId])

  return (
    <DataTable
      rows={tickets}
      columns={columns}
      getRowKey={(ticket) => ticket.id}
      loading={loading}
      error={error}
      onRetry={onRetry}
      emptyMessage={emptyMessage}
      page={page}
      pageSize={pageSize}
      total={total}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      onRowClick={onTicketClick}
      mode={mode}
      renderCard={(ticket) => <TicketCardContent ticket={ticket} showAssignee={showAssignee} />}
      pagerPosition="both"
      toolbar={toolbar}
    />
  )
}
